// /pages/admin/UserList.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../AxiosInstance';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Button, Typography, TextField, FormControlLabel, Checkbox, Box
} from '@mui/material';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const filterInputRef = useRef(null);
  const navigate = useNavigate();

  // Focus the filter input when filters are enabled
  useEffect(() => {
    if (showFilters && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [showFilters]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(0);
  }, [showFilters, searchFilter]);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchFilter]);

  const fetchUsers = async () => {
    try {
      const searchParam = showFilters && searchFilter.trim() ? `&search=${encodeURIComponent(searchFilter.trim())}` : '';
      const response = await axiosInstance.get(`/admin/users?page=${page + 1}&limit=${rowsPerPage}${searchParam}`);
      console.log(response);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUserClick = (userId) => {
    navigate(`/dashboard/admin/users/${userId}/activity`);
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', padding: 2 }}>
      <Typography variant="h4" gutterBottom>User List</Typography>
      
      {/* Filter Controls */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showFilters}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setShowFilters(isChecked);
                if (!isChecked) {
                  setSearchFilter('');
                }
              }}
              color="primary"
            />
          }
          label="Enable Search"
        />
        {showFilters && (
          <TextField
            label="Search Users"
            variant="outlined"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search by username, shop name, email, or contact..."
            sx={{ maxWidth: 500, width: '100%' }}
            inputRef={filterInputRef}
            size="small"
          />
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Shop Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.shop_name || 'N/A'}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.contact}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    onClick={() => handleUserClick(user.id)}
                  >
                    View Activity
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserList;
